import { useState } from 'react';
import AdminSidebar from '@/components/AdminSidebar';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { Menu, Plus, Trash2, ExternalLink, X, ChevronUp, ChevronDown, Loader2 } from 'lucide-react';
import { useQuery, useQueryClient } from 'react-query';
import { getAllNavigationItems, createNavigationItem, updateNavigationItem, deleteNavigationItem, reorderNavigationItems, type NavigationItem } from '@/api/navigationService';
import { useToast } from '@/components/ui/use-toast';

const LOCATIONS = [
  { id: 'header', label: 'Header' },
  { id: 'footer_col1', label: 'Footer kolom 1' },
  { id: 'footer_col2', label: 'Footer kolom 2' },
  { id: 'footer_legal', label: 'Footer legal' },
];

export default function CmsNavigationEditor() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeLocation, setActiveLocation] = useState('header');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newItem, setNewItem] = useState({ label: '', url: '', isExternal: false });
  const [showAdd, setShowAdd] = useState(false);

  const { data: allItems = [], isLoading } = useQuery('navigation-items-all', getAllNavigationItems);
  const items = allItems.filter((i) => i.location === activeLocation);

  const handleAdd = async () => {
    if (!newItem.label.trim() || !newItem.url.trim()) return;
    try {
      await createNavigationItem({ label: newItem.label, url: newItem.url, location: activeLocation, isExternal: newItem.isExternal, sortOrder: items.length + 1 });
      queryClient.invalidateQueries('navigation-items-all');
      setNewItem({ label: '', url: '', isExternal: false });
      setShowAdd(false);
    } catch (err) { toast({ variant: 'destructive', title: 'Toevoegen mislukt', description: String(err) }); }
  };

  const handleUpdate = async (id: string, updates: Partial<NavigationItem>) => {
    try {
      await updateNavigationItem(id, updates);
      queryClient.invalidateQueries('navigation-items-all');
      setEditingId(null);
    } catch (err) { toast({ variant: 'destructive', title: 'Bijwerken mislukt', description: String(err) }); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Menu-item verwijderen?')) return;
    try {
      await deleteNavigationItem(id);
      queryClient.invalidateQueries('navigation-items-all');
    } catch (err) { toast({ variant: 'destructive', title: 'Verwijderen mislukt', description: String(err) }); }
  };

  const moveItem = async (index: number, direction: 'up' | 'down') => {
    const newItems = [...items];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newItems.length) return;
    [newItems[index], newItems[swapIndex]] = [newItems[swapIndex], newItems[index]];
    await reorderNavigationItems(newItems.map((i) => i.id));
    queryClient.invalidateQueries('navigation-items-all');
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AdminSidebar />
        <main className="flex-1 overflow-auto bg-[#f6f6f7]">
          <div className="border-b border-gray-200 bg-white px-8 py-6">
            <div className="max-w-4xl mx-auto flex items-center gap-3">
              <SidebarTrigger className="md:hidden"><Menu className="h-5 w-5" /></SidebarTrigger>
              <div>
                <h1 className="text-xl font-semibold text-[#202223]">Navigatie</h1>
                <p className="text-sm text-[#6d7175] mt-0.5">Beheer het menu en de footer links</p>
              </div>
            </div>
          </div>

          <div className="px-8 py-8">
            <div className="max-w-4xl mx-auto">
              {/* Tabs */}
              <div className="flex gap-1 mb-6 border-b border-gray-200">
                {LOCATIONS.map((loc) => (
                  <button
                    key={loc.id}
                    onClick={() => setActiveLocation(loc.id)}
                    className={`px-4 py-2.5 text-[13px] font-medium border-b-2 -mb-px transition-colors ${
                      activeLocation === loc.id
                        ? 'border-[#202223] text-[#202223]'
                        : 'border-transparent text-[#6d7175] hover:text-[#202223]'
                    }`}
                  >
                    {loc.label}
                  </button>
                ))}
              </div>

              {/* Items */}
              <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] border border-gray-200/60">
                {isLoading ? (
                  <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
                ) : items.length === 0 ? (
                  <div className="py-12 text-center text-[#6d7175] text-sm">Geen menu-items voor deze locatie</div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {items.map((item, index) => (
                      <div key={item.id} className="flex items-center gap-3 px-5 py-3.5">
                        <div className="flex flex-col gap-0 shrink-0">
                          <button onClick={() => moveItem(index, 'up')} disabled={index === 0} className="text-gray-300 hover:text-gray-500 disabled:opacity-20 p-0.5"><ChevronUp className="h-3 w-3" /></button>
                          <button onClick={() => moveItem(index, 'down')} disabled={index === items.length - 1} className="text-gray-300 hover:text-gray-500 disabled:opacity-20 p-0.5"><ChevronDown className="h-3 w-3" /></button>
                        </div>
                        <div className="flex-1 min-w-0">
                          {editingId === item.id ? (
                            <div className="flex gap-2">
                              <input defaultValue={item.label} onBlur={(e) => handleUpdate(item.id, { label: e.target.value })} className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-gray-300" autoFocus />
                              <input defaultValue={item.url} onBlur={(e) => handleUpdate(item.id, { url: e.target.value })} className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 font-mono text-xs outline-none focus:border-gray-300" />
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 cursor-pointer" onClick={() => setEditingId(item.id)}>
                              <span className="text-sm font-medium text-[#202223]">{item.label}</span>
                              <span className="text-xs text-[#6d7175] font-mono">{item.url}</span>
                              {item.isExternal && <ExternalLink className="h-3 w-3 text-gray-400" />}
                            </div>
                          )}
                        </div>
                        <label className="flex items-center gap-1.5 text-xs text-[#6d7175] shrink-0">
                          <input type="checkbox" checked={item.isActive} onChange={() => handleUpdate(item.id, { isActive: !item.isActive })} className="rounded border-gray-300" />
                          Actief
                        </label>
                        <button onClick={() => handleDelete(item.id)} className="text-gray-300 hover:text-red-500 shrink-0 p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="border-t border-gray-100 px-5 py-3.5">
                  {showAdd ? (
                    <div className="flex gap-2 items-center">
                      <input value={newItem.label} onChange={(e) => setNewItem({ ...newItem, label: e.target.value })} placeholder="Label" className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-gray-300" autoFocus />
                      <input value={newItem.url} onChange={(e) => setNewItem({ ...newItem, url: e.target.value })} placeholder="/pad of https://..." className="flex-1 border border-gray-200 rounded-lg px-3 py-2 font-mono text-xs outline-none focus:border-gray-300" />
                      <label className="flex items-center gap-1.5 text-xs text-[#6d7175] whitespace-nowrap">
                        <input type="checkbox" checked={newItem.isExternal} onChange={(e) => setNewItem({ ...newItem, isExternal: e.target.checked })} className="rounded border-gray-300" />
                        Extern
                      </label>
                      <button onClick={handleAdd} className="bg-[#202223] text-white px-3.5 py-2 rounded-lg text-[13px] font-medium hover:bg-[#333]">Toevoegen</button>
                      <button onClick={() => setShowAdd(false)} className="text-gray-400 hover:text-gray-600 p-1"><X className="h-4 w-4" /></button>
                    </div>
                  ) : (
                    <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 text-[13px] font-medium text-[#202223] hover:text-[#333]">
                      <Plus className="h-4 w-4" /> Item toevoegen
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
